import React, { useState, useContext } from 'react';
import { Button, Card, FileUpload } from '@scalingworks/react-admin-ui';
import { FaPlus, FaTrash, FaTrophy, FaGift, FaEdit, FaCheckCircle, FaSearch } from 'react-icons/fa';
import { MdAutoAwesome } from 'react-icons/md';
import { useTranslate } from '@refinedev/core';
import type { MilestoneRewardType } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { RewardSelector, type RewardOption } from '../RewardSelector';
import { useCreateAssetsMutation } from '~/generated/hooks';
import { useRewardNames } from '../hooks/useRewardNames';
import { STAMP_CONFIG } from '~/resources/loyalty/stamp-template/config/stamp-config';
import { formatCentsToRinggit } from '~/utils/currency';
import { CurrencyInput } from '~/components/CurrencyInput';
import { ChannelContext } from '~/providers/Channel/context';
import { startCase } from 'lodash';

export interface Milestone {
  stampsRequired?: number; // Frontend uses this
  milestoneStamps?: number; // Backend uses this
  name?: string;
  rewardType: string;
  rewardValue: string; // Always a string - stores reward ID for VOUCHER/PRODUCT, or value for others
  autoClaimable: boolean;
  description?: string;
  iconUrl?: string;
}

interface MilestoneManagerProps {
  milestones: Milestone[];
  onChange: (milestones: Milestone[]) => void;
  totalStampsRequired: number;
  rewardTypes: MilestoneRewardType[];
  errors?: any;
  disabled?: boolean;
}

export const MilestoneManager: React.FC<MilestoneManagerProps> = ({
  milestones = [],
  onChange,
  totalStampsRequired,
  rewardTypes,
  errors,
  disabled = false,
}) => {
  const t = useTranslate();
  const { channel } = useContext(ChannelContext);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Milestone | null>(null);
  const [showRewardSelector, setShowRewardSelector] = useState(false);
  const [selectedRewardName, setSelectedRewardName] = useState<string>(''); // For display purposes
  const [isAddingNew, setIsAddingNew] = useState(false); // Track if we're adding a new milestone
  const [uploadingMilestoneIcon, setUploadingMilestoneIcon] = useState(false);

  // Extract reward IDs from milestones for name resolution
  // Include the currently editing milestone's reward ID as well
  const rewardIds = [
    ...milestones
      .filter((m) => m.rewardType === 'REWARD' && m.rewardValue)
      .map((m) => m.rewardValue!),
    ...(editForm?.rewardType === 'REWARD' && editForm?.rewardValue ? [editForm.rewardValue] : []),
  ].filter(Boolean);

  const { getRewardName, loading: loadingRewards } = useRewardNames(rewardIds);

  // Update selectedRewardName when editing a milestone with a reward
  React.useEffect(() => {
    if (editForm?.rewardType === 'REWARD' && editForm?.rewardValue && !selectedRewardName) {
      const name = getRewardName(editForm.rewardValue);
      if (name !== editForm.rewardValue) {
        setSelectedRewardName(name);
      }
    }
  }, [editForm?.rewardValue, editForm?.rewardType, getRewardName, selectedRewardName]);

  // Get loyalty translations from channel
  const starTranslation = channel?.loyaltyTranslations?.starTranslation || 'EXP';
  const pointTranslation = channel?.loyaltyTranslations?.pointTranslation || 'Point';

  // Helper function to format reward type label
  const formatRewardTypeLabel = (rewardType: string): string => {
    const labelMap: Record<string, string> = {
      STORE_CREDIT: 'Store Credit',
      EWALLET: 'eWallet',
      VOUCHER: 'Voucher',
      REWARD: 'Reward',
      DISCOUNT: 'Discount',
      TIER_UPGRADE: 'Tier Upgrade',
      EXPERIENCE: `Experience (${startCase(starTranslation)})`,
      POINTS: `Points (${startCase(pointTranslation)})`,
    };

    return labelMap[rewardType] || rewardType;
  };

  // Helper function to get reward display value
  const getRewardDisplayValue = (rewardId: string | undefined): string => {
    if (!rewardId) return '';
    if (loadingRewards) return 'Loading...';
    const name = getRewardName(rewardId);
    // Only return the name if it's different from the ID (meaning it was successfully resolved)
    return name !== rewardId ? name : 'Loading...';
  };

  // Helper function to format milestone reward value for display
  const formatMilestoneRewardValue = (milestone: Milestone): string => {
    const { rewardType, rewardValue } = milestone;

    if (!rewardValue) return '';

    switch (rewardType) {
      case 'REWARD':
        return getRewardDisplayValue(rewardValue);
      case 'VOUCHER':
        return `${t('milestone.voucher')}: ${rewardValue}`;
      case 'STORE_CREDIT':
        return `RM ${formatCentsToRinggit(Number(rewardValue))}`;
      case 'POINTS':
        return `${rewardValue} PTS`; // Always display as "X PTS"
      case 'EXPERIENCE':
        return `${rewardValue} ${startCase(starTranslation)}`; // e.g., "100 Bean" or "100 EXP"
      case 'EWALLET':
        return `RM ${formatCentsToRinggit(Number(rewardValue))}`;
      default:
        return `${t('milestone.value')}: ${rewardValue}`;
    }
  };

  // Helper function to get stamps required from milestone
  const getStampsRequired = (milestone: Milestone): number => {
    return milestone.stampsRequired || milestone.milestoneStamps || 0;
  };

  // Helper function to render currency input field
  const renderCurrencyInput = (
    value: string | undefined,
    onChange: (value: string) => void,
    disabled: boolean = false
  ) => (
    <CurrencyInput
      value={Number(value || 0)}
      onChange={(newValue) => onChange(newValue.toString())}
      placeholder="0.00"
      disabled={disabled}
    />
  );

  // Helper function to render reward value input based on type
  const renderRewardValueInput = (editForm: Milestone, disabled: boolean = false) => {
    const { rewardType, rewardValue } = editForm;

    if (rewardType === 'VOUCHER' || rewardType === 'REWARD') {
      return (
        <button
          type="button"
          onClick={() => setShowRewardSelector(true)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white hover:bg-gray-50 flex items-center justify-between"
          disabled={disabled}
        >
          <span className={rewardValue ? 'text-gray-800' : 'text-gray-400'}>
            {selectedRewardName || rewardValue || `Select a ${rewardType.toLowerCase()}...`}
          </span>
          <FaSearch className="text-gray-400" />
        </button>
      );
    }

    if (rewardType === 'STORE_CREDIT' || rewardType === 'EWALLET') {
      return renderCurrencyInput(
        rewardValue,
        (value) => updateEditForm('rewardValue', value),
        disabled
      );
    }

    return (
      <input
        type="number"
        value={rewardValue || ''}
        onChange={(e) => updateEditForm('rewardValue', e.target.value)}
        placeholder="Amount"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        disabled={disabled}
      />
    );
  };

  // Use createAssets mutation for uploading milestone icons
  const { mutate: createAssets } = useCreateAssetsMutation({
    onSuccess: (data) => {
      const results = data.createAssets;
      if (results && results.length > 0) {
        const asset = results[0];
        if ('source' in asset && editForm) {
          // Update the edit form with the new icon URL
          setEditForm({
            ...editForm,
            iconUrl: asset.source,
          });
          setUploadingMilestoneIcon(false);
        } else if ('errorCode' in asset) {
          console.error('Failed to upload milestone icon:', asset.message);
          setUploadingMilestoneIcon(false);
        }
      }
    },
    onError: (error) => {
      console.error('Failed to create milestone icon asset:', error);
      setUploadingMilestoneIcon(false);
    },
  });

  const handleMilestoneIconUpload = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setUploadingMilestoneIcon(true);
      createAssets({
        input: [
          {
            file: file as any,
            tags: ['milestone-icon'],
          },
        ],
      });
    }
  };

  // Sort milestones by stamps required
  const sortedMilestones = [...milestones].sort(
    (a, b) => getStampsRequired(a) - getStampsRequired(b)
  );

  // Start adding a new milestone
  const handleAddMilestone = () => {
    const stamps = Math.floor(totalStampsRequired / 2);
    const newMilestone: Milestone = {
      stampsRequired: stamps,
      milestoneStamps: stamps,
      name: '',
      rewardType: 'POINTS',
      rewardValue: '', // Always string
      autoClaimable: STAMP_CONFIG.milestones.forceAutoClaim || true, // Force auto-claim if configured
      description: '',
    };
    setIsAddingNew(true);
    setEditingIndex(null); // Not editing existing
    setEditForm(newMilestone);
  };

  // Remove a milestone
  const handleRemoveMilestone = (visualIndex: number) => {
    // Get the milestone from the sorted array
    const milestone = sortedMilestones[visualIndex];
    // Find the actual index in the original milestones array
    const actualIndex = milestones.findIndex(
      (m) =>
        getStampsRequired(m) === getStampsRequired(milestone) &&
        m.name === milestone.name &&
        m.rewardType === milestone.rewardType
    );

    const updatedMilestones = milestones.filter((_, i) => i !== actualIndex);
    onChange(updatedMilestones);
    if (editingIndex === actualIndex) {
      setEditingIndex(null);
      setEditForm(null);
    }
  };

  // Start editing a milestone
  const handleEditMilestone = (visualIndex: number) => {
    // Get the milestone from the sorted array
    const milestone = sortedMilestones[visualIndex];
    // Find the actual index in the original milestones array
    const actualIndex = milestones.findIndex(
      (m) =>
        getStampsRequired(m) === getStampsRequired(milestone) &&
        m.name === milestone.name &&
        m.rewardType === milestone.rewardType
    );

    setEditingIndex(actualIndex);
    // Ensure both fields are set when starting edit
    const formData = {
      ...milestone,
      stampsRequired: milestone.stampsRequired || milestone.milestoneStamps || 0,
      milestoneStamps: milestone.milestoneStamps || milestone.stampsRequired || 0,
      // Force auto-claim if configured
      autoClaimable: STAMP_CONFIG.milestones.forceAutoClaim
        ? true
        : milestone.autoClaimable ?? true,
    };
    setEditForm(formData);
    setSelectedRewardName(''); // Reset selected reward name, will be set by useEffect
  };

  // Save edited or new milestone
  const handleSaveMilestone = () => {
    if (editForm) {
      // Ensure autoClaimable is set to true if forceAutoClaim is enabled
      const milestoneToSave = {
        ...editForm,
        autoClaimable: STAMP_CONFIG.milestones.forceAutoClaim
          ? true
          : editForm.autoClaimable ?? true,
      };

      if (isAddingNew) {
        // Adding a new milestone
        onChange([...milestones, milestoneToSave]);
        setIsAddingNew(false);
      } else if (editingIndex !== null) {
        // Editing existing milestone
        const updatedMilestones = [...milestones];
        updatedMilestones[editingIndex] = milestoneToSave;
        onChange(updatedMilestones);
      }
      setEditingIndex(null);
      setEditForm(null);
      setSelectedRewardName(''); // Reset selected reward name
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm(null);
    setIsAddingNew(false);
    setSelectedRewardName(''); // Reset selected reward name
  };

  // Update edit form field
  const updateEditForm = (field: keyof Milestone, value: any) => {
    if (editForm) {
      const updatedForm = { ...editForm, [field]: value };
      setEditForm(updatedForm);
    }
  };

  // Handle reward selection from modal
  const handleRewardSelect = (reward: RewardOption) => {
    if (editForm) {
      setEditForm({
        ...editForm,
        rewardValue: reward.id, // Store the reward ID in rewardValue
      });
      setSelectedRewardName(reward.name); // Store name for display
    }
    setShowRewardSelector(false);
  };

  // Get reward type configuration
  const getRewardType = (value: string) => {
    return rewardTypes.find((rt) => rt.value === value);
  };

  return (
    <div className="space-y-4">
      {/* New Milestone Form (shown when adding) */}
      {isAddingNew && editForm && (
        <Card className="p-4 border-2 border-indigo-300 bg-indigo-50">
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <FaPlus className="text-indigo-600" />
              <h4 className="font-semibold text-indigo-800">Add New Milestone</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Milestone Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => updateEditForm('name', e.target.value)}
                placeholder="e.g., Bronze Reward, 5 Stamp Bonus"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={disabled}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stamps Required <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={
                    editForm.stampsRequired !== undefined
                      ? editForm.stampsRequired
                      : editForm.milestoneStamps || 0
                  }
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                    setEditForm({
                      ...editForm,
                      stampsRequired: value,
                      milestoneStamps: value,
                    });
                  }}
                  min={1}
                  max={totalStampsRequired}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={disabled}
                />
                {(editForm.stampsRequired || editForm.milestoneStamps || 0) >
                  totalStampsRequired && (
                  <p className="text-xs text-red-600 mt-1">
                    Must not exceed total stamps ({totalStampsRequired})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={editForm.rewardType}
                  onChange={(e) => {
                    updateEditForm('rewardType', e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={disabled}
                >
                  {rewardTypes.map((rt) => (
                    <option key={rt.value} value={rt.value}>
                      {formatRewardTypeLabel(rt.value)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {getRewardType(editForm.rewardType)?.requiresValue && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editForm.rewardType === 'VOUCHER' || editForm.rewardType === 'REWARD'
                    ? 'Select Reward'
                    : 'Reward Value'}
                </label>
                {renderRewardValueInput(editForm, disabled)}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Milestone Icon (Optional)
              </label>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <FileUpload
                    onFilesSelected={handleMilestoneIconUpload}
                    fileType="image"
                    variant="button"
                    label="Upload Icon"
                    hint="Upload milestone icon"
                    disabled={disabled || uploadingMilestoneIcon}
                  />
                  {editForm.iconUrl && (
                    <div className="flex items-center gap-2">
                      <img
                        src={editForm.iconUrl}
                        alt="Milestone icon preview"
                        className="w-10 h-10 object-contain border rounded"
                      />
                      <button
                        type="button"
                        onClick={() => updateEditForm('iconUrl', '')}
                        className="text-sm text-red-600 hover:text-red-700"
                        disabled={disabled}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                {uploadingMilestoneIcon && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    <span>Uploading milestone icon...</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Custom icon to display at this milestone position
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <input
                type="text"
                value={editForm.description || ''}
                onChange={(e) => updateEditForm('description', e.target.value)}
                placeholder="e.g., Halfway bonus reward"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={disabled}
              />
            </div>

            {/* Auto-claim checkbox - hidden when forceAutoClaim is true */}
            {!STAMP_CONFIG.milestones.forceAutoClaim &&
              STAMP_CONFIG.milestones.showAutoClaimOption && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.autoClaimable}
                      onChange={(e) => updateEditForm('autoClaimable', e.target.checked)}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                      disabled={disabled}
                    />
                    <span className="text-sm font-medium text-gray-700">Auto-claim reward</span>
                  </label>
                </div>
              )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="default" size="sm" onClick={handleCancelEdit} disabled={disabled}>
                Cancel
              </Button>
              <Button
                variant="solid"
                size="sm"
                onClick={handleSaveMilestone}
                disabled={
                  disabled ||
                  !editForm.name ||
                  getStampsRequired(editForm) > totalStampsRequired ||
                  (rewardTypes.find((rt) => rt.value === editForm.rewardType)?.requiresValue &&
                    !editForm.rewardValue)
                }
              >
                <div className="flex items-center">
                  <FaCheckCircle className="mr-1" />
                  Save Milestone
                </div>
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Milestones List */}
      <div className="space-y-3">
        {sortedMilestones.map((milestone, visualIndex) => {
          // Find the actual index in the original milestones array
          const actualIndex = milestones.findIndex(
            (m) =>
              (m.stampsRequired || m.milestoneStamps) ===
                (milestone.stampsRequired || milestone.milestoneStamps) &&
              m.name === milestone.name &&
              m.rewardType === milestone.rewardType
          );
          const isEditing = editingIndex === actualIndex;
          const rewardType = getRewardType(milestone.rewardType);

          return (
            <Card
              key={`milestone-${actualIndex}-${
                milestone.stampsRequired || milestone.milestoneStamps
              }`}
              className="p-4 hover:shadow-md transition-shadow"
            >
              {isEditing && editForm ? (
                // Edit Mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Milestone Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={(e) => updateEditForm('name', e.target.value)}
                      placeholder="e.g., Bronze Reward, 5 Stamp Bonus"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={disabled}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stamps Required
                      </label>
                      <input
                        type="number"
                        value={
                          editForm.stampsRequired !== undefined
                            ? editForm.stampsRequired
                            : editForm.milestoneStamps || 0
                        }
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                          setEditForm({
                            ...editForm,
                            stampsRequired: value,
                            milestoneStamps: value,
                          });
                        }}
                        min={1}
                        max={totalStampsRequired}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={disabled}
                      />
                      {(editForm.stampsRequired || editForm.milestoneStamps || 0) >
                        totalStampsRequired && (
                        <p className="text-xs text-red-600 mt-1">
                          Must not exceed total stamps ({totalStampsRequired})
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reward Type
                      </label>
                      <select
                        value={editForm.rewardType}
                        onChange={(e) => {
                          updateEditForm('rewardType', e.target.value);
                          // if (!isEmpty(e.target.value)) {
                          //   updateEditForm('rewardValue', '')
                          // }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={disabled}
                      >
                        {rewardTypes.map((rt) => (
                          <option key={rt.value} value={rt.value}>
                            {formatRewardTypeLabel(rt.value)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {getRewardType(editForm.rewardType)?.requiresValue && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {editForm.rewardType === 'VOUCHER' || editForm.rewardType === 'REWARD'
                          ? 'Select Reward'
                          : 'Reward Value'}
                      </label>
                      {renderRewardValueInput(editForm, disabled)}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Milestone Icon (Optional)
                    </label>
                    <div className="space-y-3">
                      <div className="flex gap-3 items-start">
                        <FileUpload
                          onFilesSelected={handleMilestoneIconUpload}
                          fileType="image"
                          variant="button"
                          label="Upload Icon"
                          hint="Upload milestone icon"
                          disabled={disabled || uploadingMilestoneIcon}
                        />
                        {editForm.iconUrl && (
                          <div className="flex items-center gap-2">
                            <img
                              src={editForm.iconUrl}
                              alt="Milestone icon preview"
                              className="w-10 h-10 object-contain border rounded"
                            />
                            <button
                              type="button"
                              onClick={() => updateEditForm('iconUrl', '')}
                              className="text-sm text-red-600 hover:text-red-700"
                              disabled={disabled}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                      {uploadingMilestoneIcon && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          <span>Uploading milestone icon...</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        Custom icon to display at this milestone position
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <input
                      type="text"
                      value={editForm.description || ''}
                      onChange={(e) => updateEditForm('description', e.target.value)}
                      placeholder="e.g., Halfway bonus reward"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={disabled}
                    />
                  </div>

                  {/* Auto-claim checkbox - hidden when forceAutoClaim is true */}
                  {!STAMP_CONFIG.milestones.forceAutoClaim &&
                    STAMP_CONFIG.milestones.showAutoClaimOption && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.autoClaimable}
                            onChange={(e) => updateEditForm('autoClaimable', e.target.checked)}
                            className="rounded text-indigo-600 focus:ring-indigo-500"
                            disabled={disabled}
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Auto-claim reward
                          </span>
                        </label>
                      </div>
                    )}

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={disabled}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={handleSaveMilestone}
                      disabled={
                        disabled ||
                        !editForm.name ||
                        (editForm.stampsRequired || editForm.milestoneStamps || 0) >
                          totalStampsRequired ||
                        (rewardTypes.find((rt) => rt.value === editForm.rewardType)
                          ?.requiresValue &&
                          !editForm.rewardValue)
                      }
                    >
                      <div className="flex items-center">
                        <FaCheckCircle className="mr-1" />
                        <p>Save</p>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center justify-center bg-yellow-100 rounded-lg p-3 min-w-[60px]">
                      {milestone.iconUrl ? (
                        <img
                          src={milestone.iconUrl}
                          alt="Milestone icon"
                          className="w-8 h-8 mb-1 object-fill rounded-full"
                        />
                      ) : (
                        <FaTrophy className="text-yellow-600 text-xl mb-1" />
                      )}
                      <span className="text-xs font-bold text-yellow-800">
                        {getStampsRequired(milestone)}
                      </span>
                      <span className="text-xs text-yellow-600">Milestone</span>
                      <span className="text-xs text-yellow-600">Stamps</span>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-800">
                          {milestone.name || `Milestone at ${getStampsRequired(milestone)} stamps`}
                        </span>
                        {milestone.autoClaimable && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Auto-claim
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="block text-gray-500">
                          {formatRewardTypeLabel(milestone.rewardType)}
                        </span>
                        {rewardType?.requiresValue && milestone.rewardValue && (
                          <span className="font-medium">
                            {formatMilestoneRewardValue(milestone)}
                          </span>
                        )}
                        {milestone.description && (
                          <span className="block text-xs text-gray-500 mt-1">
                            {milestone.description}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!disabled && (
                      <>
                        <button
                          onClick={() => handleEditMilestone(visualIndex)}
                          className="p-2 text-gray-600 hover:text-indigo-600 transition-colors"
                          title="Edit milestone"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleRemoveMilestone(visualIndex)}
                          className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                          title="Remove milestone"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Milestone Button */}
      {!disabled && !isAddingNew && (
        <div className="flex justify-center pt-2">
          <Button
            variant="default"
            onClick={handleAddMilestone}
            className="flex items-center gap-2"
          >
            <FaPlus />
            Add Milestone
          </Button>
        </div>
      )}

      {/* Empty State */}
      {milestones.length === 0 && !isAddingNew && (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FaGift className="text-4xl text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-3">No milestones configured</p>
          <p className="text-xs text-gray-500 mb-4">
            Add milestones to reward customers before they complete the full stamp card
          </p>
          {!disabled && (
            <Button variant="solid" size="sm" onClick={handleAddMilestone}>
              <div className="flex items-center">
                <MdAutoAwesome className="mr-1" />
                Add First Milestone
              </div>
            </Button>
          )}
        </div>
      )}

      {/* Information Box */}
      {milestones.length > 0 && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800 mb-2">Milestone Tips:</p>
          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
            <li>Set milestones at strategic points to maintain customer engagement</li>
            <li>Auto-claim rewards are granted automatically when reached</li>
            <li>Manual rewards require customer action to claim</li>
            <li>Consider offering increasing value as stamps accumulate</li>
          </ul>
        </div>
      )}

      {/* Error Message */}
      {errors && typeof errors === 'string' && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xs text-red-600 flex items-center gap-1">
            <span>⚠️</span>
            {errors}
          </p>
        </div>
      )}

      {/* Reward Selector Modal */}
      <RewardSelector
        isOpen={showRewardSelector}
        onClose={() => setShowRewardSelector(false)}
        onSelect={handleRewardSelect}
        selectedRewardId={editForm?.rewardValue}
      />
    </div>
  );
};

export default MilestoneManager;
